import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HiEye, HiPencil, HiTrash } from 'react-icons/hi2';
import { calculateRiskLevel } from '../../services/productAuditService';
import AuditDetailsModal from './AuditDetailsModal';
import EditAuditForm from './EditAuditForm';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

const AuditTable = ({ audits, onDelete, onEdit, loading }) => {
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [auditToDelete, setAuditToDelete] = useState(null);

  const formatDate = date => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRiskColor = riskLevel => {
    switch (riskLevel) {
      case 'Critical':
        return 'bg-red-100 text-red-800';
      case 'High':
        return 'bg-orange-100 text-orange-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleView = audit => {
    setSelectedAudit(audit);
    setViewModalOpen(true);
  };

  const handleEdit = audit => {
    setSelectedAudit(audit);
    setEditModalOpen(true);
  };

  const handleDeleteClick = audit => {
    setAuditToDelete(audit);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (auditToDelete) {
      await onDelete(auditToDelete.id);
      setDeleteDialogOpen(false);
      setAuditToDelete(null);
    }
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setSelectedAudit(null);
    if (onEdit) onEdit();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <p className="mt-4 text-sm text-gray-500">Loading audits...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Submission Title
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Organization
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Stage
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Risk Level
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Recommendations
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {audits.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                  No audits found
                </td>
              </tr>
            ) : (
              audits.map((audit, index) => {
                const riskLevel = calculateRiskLevel(audit);
                const title = audit.submissionTitle || audit.productName || 'Untitled';
                const organization = audit.organization || 'N/A';
                return (
                  <motion.tr
                    key={audit.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-black">
                      {title}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                      {organization}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                      {audit.projectStage}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                      {formatDate(audit.auditDate)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getRiskColor(riskLevel)}`}
                      >
                        {riskLevel}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      <div className="max-w-xs truncate">{audit.recommendations || 'N/A'}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleView(audit)}
                          className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                          title="View"
                        >
                          <HiEye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(audit)}
                          className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
                          title="Edit"
                        >
                          <HiPencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(audit)}
                          className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                          title="Delete"
                        >
                          <HiTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <AuditDetailsModal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedAudit(null);
        }}
        audit={selectedAudit}
      />

      <EditAuditForm
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedAudit(null);
        }}
        audit={selectedAudit}
        onSuccess={handleEditSuccess}
      />

      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setAuditToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName="Audit"
      />
    </>
  );
};

export default AuditTable;
